import { safeJSONparse } from "@av/utils/safe-json-parse";
import { getLocalizedJobPostContent } from "./localized-job-post-content";

type SearchableJobPost = {
  jobPostId?: string;
  descriptions?: Record<string, string>;
};

type EditorNode = {
  text?: string;
  content?: EditorNode[];
};

// Deliberately narrow: plain text may open with a bracket, editor content always
// opens with a typed node.
const EDITOR_JSON_START = /^\s*\[?\s*\{\s*"type"\s*:/;
const HTML_TAG = /<[^>]*>/g;
const WHITESPACE = /\s+/g;

// Block elements are a closed set; inline ones are not, so an unknown tag defaults to
// inline and joins a word rather than splitting one a candidate reads as whole.
const BLOCK_TAGS = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "BR",
  "DD",
  "DIV",
  "DL",
  "DT",
  "FIGCAPTION",
  "FIGURE",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "HR",
  "LI",
  "MAIN",
  "NAV",
  "OL",
  "P",
  "PRE",
  "SECTION",
  "TABLE",
  "TD",
  "TH",
  "TR",
  "UL",
]);

const NON_RENDERED_TAGS = new Set(["NOSCRIPT", "SCRIPT", "STYLE", "TEMPLATE"]);

/**
 * Descriptions are stored as editor JSON, HTML, or plain text, so a search has to match
 * the rendered text rather than the stored string, which would hit markup such as "paragraph"
 * or "h4" on every post. The editor's own renderer is not used here: it rebuilds a ProseMirror
 * schema per description (and the HTML shape pays for two), which is too much work to run
 * across a whole board on the first keystroke.
 */
export function buildJobPostDescriptionSearchIndex(
  jobPosts: SearchableJobPost[],
  locale: string
): Map<string, string> {
  const index = new Map<string, string>();

  for (const jobPost of jobPosts) {
    if (!jobPost.jobPostId) {
      continue;
    }

    index.set(jobPost.jobPostId, toSearchableText(jobPost.descriptions, locale));
  }

  return index;
}

function toSearchableText(descriptions: Record<string, string> | undefined, locale: string) {
  const description = getLocalizedJobPostContent(descriptions, locale);

  if (!description) {
    return "";
  }

  const parsed = safeJSONparse(description);

  if (!parsed && EDITOR_JSON_START.test(description)) {
    // Falling back to the HTML path would index the raw JSON, so say so rather than
    // leaving the description quietly unsearchable.
    console.error("Could not read a job description as editor content for search");

    return "";
  }

  try {
    const text = parsed ? getEditorNodeText(parsed) : getHtmlText(description);

    return text.replace(WHITESPACE, " ").trim().toLocaleLowerCase();
  } catch (error) {
    // One corrupt description must not take the whole board's search down with it.
    console.error("Could not read a job description for search", error);

    return "";
  }
}

function getEditorNodeText(parsed: object): string {
  const root: EditorNode = Array.isArray(parsed) ? { content: parsed } : parsed;
  const parts: string[] = [];

  collectEditorNodeText(root, parts);

  return parts.join("");
}

function collectEditorNodeText(node: unknown, parts: string[]) {
  // Valid JSON is not necessarily a valid node: content arrays can hold nulls and
  // primitives, which contribute no text.
  if (typeof node !== "object" || node === null) {
    return;
  }

  const { text, content } = node as EditorNode;

  if (typeof text === "string") {
    parts.push(text);

    return;
  }

  if (Array.isArray(content)) {
    for (const child of content) {
      collectEditorNodeText(child, parts);
    }
  }

  // Marks split a word into adjacent text nodes, so only non-text nodes separate words.
  parts.push(" ");
}

function getHtmlText(description: string) {
  if (typeof DOMParser === "undefined") {
    return description.replace(HTML_TAG, " ");
  }

  const parts: string[] = [];

  collectHtmlText(new DOMParser().parseFromString(description, "text/html").body, parts);

  return parts.join("");
}

function collectHtmlText(node: Node, parts: string[]) {
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      parts.push(child.nodeValue ?? "");

      continue;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    const element = child as Element;

    if (NON_RENDERED_TAGS.has(element.tagName)) {
      continue;
    }

    collectHtmlText(element, parts);

    if (BLOCK_TAGS.has(element.tagName)) {
      parts.push(" ");
    }
  }
}
